import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../services/api';

function TeamDirectory() {
  const [teamProfiles, setTeamProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAccepting, setFilterAccepting] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Fetch team profiles
  useEffect(() => {
    const fetchTeamProfiles = async () => {
      try {
        setLoading(true);
        const response = await API.get('/therapist/team-profiles');
        setTeamProfiles(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching team profiles:', err);
        setError('Unable to load team profiles. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTeamProfiles();
  }, []);

  // Filter profiles based on search term and accepting clients filter
  const filteredProfiles = teamProfiles.filter(profile => {
    const fullName = `${profile.first_name} ${profile.last_name}`.toLowerCase();
    const searchMatch = !searchTerm || 
      fullName.includes(searchTerm.toLowerCase()) ||
      (profile.specialties && profile.specialties.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (profile.license_type && profile.license_type.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const acceptingMatch = !filterAccepting || profile.accepting_clients;
    
    return searchMatch && acceptingMatch;
  });

  // Open profile detail modal
  const openProfileDetail = (profile) => {
    setSelectedProfile(profile);
    setShowDetailModal(true);
  };

  // Close profile detail modal
  const closeProfileDetail = () => {
    setShowDetailModal(false);
    setSelectedProfile(null);
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1>Therapy Team Directory</h1>
        <Link to="/therapist/dashboard" style={backLinkStyle}>
          &larr; Back to Dashboard
        </Link>
      </div>

      <div style={searchFilterContainerStyle}>
        <div style={searchContainerStyle}>
          <input
            type="text"
            placeholder="Search by name, specialty, or license..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <div style={filterContainerStyle}>
          <label style={filterLabelStyle}>
            <input
              type="checkbox"
              checked={filterAccepting}
              onChange={() => setFilterAccepting(!filterAccepting)}
            />
            <span style={filterTextStyle}>Show only therapists accepting new clients</span>
          </label>
        </div>
      </div>

      {loading ? (
        <div style={loadingStyle}>Loading team profiles...</div>
      ) : error ? (
        <div style={errorStyle}>{error}</div>
      ) : filteredProfiles.length === 0 ? (
        <div style={noResultsStyle}>No therapists match your search criteria.</div>
      ) : (
        <div style={profilesGridStyle}>
          {filteredProfiles.map(profile => (
            <div 
              key={profile.therapist_id} 
              style={profileCardStyle}
              onClick={() => openProfileDetail(profile)}
            >
              <div style={profileCardTopStyle}>
                <div style={profilePhotoContainerStyle}>
                  {profile.photo_url ? (
                    <img 
                      src={profile.photo_url} 
                      alt={`${profile.first_name} ${profile.last_name}`} 
                      style={profilePhotoStyle} 
                    />
                  ) : (
                    <div style={{
                      ...profilePhotoStyle,
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.5rem',
                      color: '#666'
                    }}>
                      {profile.first_name ? profile.first_name.charAt(0) : ''}
                      {profile.last_name ? profile.last_name.charAt(0) : ''}
                    </div>
                  )}
                </div>
                <div style={profileCardHeaderStyle}>
                  <h3 style={profileNameStyle}>
                    {profile.first_name} {profile.last_name}
                  </h3>
                  {profile.license_type && (
                    <div style={licenseTypeStyle}>{profile.license_type}</div>
                  )}
                </div>
              </div>

              {profile.specialties && (
                <p style={specialtiesStyle}>
                  <strong>Specialties:</strong> {profile.specialties}
                </p>
              )}

              <div style={acceptingClientsContainerStyle}>
                <div 
                  style={{
                    ...acceptingClientsIndicatorStyle,
                    backgroundColor: profile.accepting_clients ? '#4CAF50' : '#F44336'
                  }}
                ></div>
                <span style={acceptingClientsTextStyle}>
                  {profile.accepting_clients ? 'Accepting new clients' : 'Not accepting new clients'}
                </span>
              </div>

              <div style={viewProfileBtnContainerStyle}>
                <button style={viewProfileBtnStyle}>
                  View Profile
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Profile Detail Modal */}
      {showDetailModal && selectedProfile && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <div style={modalHeaderStyle}>
              <h2 style={modalTitleStyle}>
                {selectedProfile.first_name} {selectedProfile.last_name}
                {selectedProfile.license_type && (
                  <span style={modalLicenseBadgeStyle}>{selectedProfile.license_type}</span>
                )}
              </h2>
              <button 
                onClick={closeProfileDetail}
                style={modalCloseButtonStyle}
              >
                &times;
              </button>
            </div>

            <div style={modalBodyStyle}>
              <div style={modalProfileHeaderStyle}>
                <div style={modalPhotoContainerStyle}>
                  {selectedProfile.photo_url ? (
                    <img 
                      src={selectedProfile.photo_url} 
                      alt={`${selectedProfile.first_name} ${selectedProfile.last_name}`} 
                      style={modalPhotoStyle} 
                    />
                  ) : (
                    <div style={{
                      ...modalPhotoStyle,
                      backgroundColor: '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2.5rem',
                      color: '#666'
                    }}>
                      {selectedProfile.first_name ? selectedProfile.first_name.charAt(0) : ''}
                      {selectedProfile.last_name ? selectedProfile.last_name.charAt(0) : ''}
                    </div>
                  )}
                </div>

                <div style={modalProfileInfoStyle}>
                  <div style={acceptingClientsContainerStyle}>
                    <div 
                      style={{
                        ...acceptingClientsIndicatorStyle,
                        width: '12px',
                        height: '12px',
                        backgroundColor: selectedProfile.accepting_clients ? '#4CAF50' : '#F44336'
                      }}
                    ></div>
                    <span style={{...acceptingClientsTextStyle, fontSize: '1rem', fontWeight: 'bold'}}>
                      {selectedProfile.accepting_clients ? 'Accepting new clients' : 'Not accepting new clients'}
                    </span>
                  </div>

                  {!selectedProfile.accepting_clients && selectedProfile.unavailable_reason && (
                    <div style={unavailableReasonStyle}>
                      <p><strong>Reason:</strong> {selectedProfile.unavailable_reason}</p>
                    </div>
                  )}

                  {selectedProfile.license_number && (
                    <p style={detailItemStyle}><strong>License #:</strong> {selectedProfile.license_number}</p>
                  )}
                  
                  {selectedProfile.years_experience && (
                    <p style={detailItemStyle}><strong>Experience:</strong> {selectedProfile.years_experience} years</p>
                  )}
                </div>
              </div>

              <div style={modalDetailSectionStyle}>
                {selectedProfile.specialties && (
                  <div style={modalSectionStyle}>
                    <h3 style={sectionTitleStyle}>Specialties</h3>
                    <p>{selectedProfile.specialties}</p>
                  </div>
                )}

                {selectedProfile.client_demographics && (
                  <div style={modalSectionStyle}>
                    <h3 style={sectionTitleStyle}>Client Demographics</h3>
                    <p>{selectedProfile.client_demographics}</p>
                  </div>
                )}

                {selectedProfile.education && (
                  <div style={modalSectionStyle}>
                    <h3 style={sectionTitleStyle}>Education</h3>
                    <p>{selectedProfile.education}</p>
                  </div>
                )}

                {selectedProfile.bio && (
                  <div style={modalSectionStyle}>
                    <h3 style={sectionTitleStyle}>About</h3>
                    <p>{selectedProfile.bio}</p>
                  </div>
                )}

                {selectedProfile.interests && (
                  <div style={modalSectionStyle}>
                    <h3 style={sectionTitleStyle}>Personal Interests</h3>
                    <p>{selectedProfile.interests}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const containerStyle = {
  padding: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2rem'
};

const backLinkStyle = {
  textDecoration: 'none',
  color: '#4CAF50',
  fontWeight: 'bold',
  fontSize: '1rem'
};

const searchFilterContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  marginBottom: '2rem'
};

const searchContainerStyle = {
  flex: 1
};

const searchInputStyle = {
  width: '100%',
  padding: '0.75rem',
  borderRadius: '4px',
  border: '1px solid #ddd',
  fontSize: '1rem'
};

const filterContainerStyle = {
  display: 'flex',
  alignItems: 'center'
};

const filterLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer'
};

const filterTextStyle = {
  marginLeft: '0.5rem',
  fontSize: '1rem'
};

const loadingStyle = {
  textAlign: 'center',
  padding: '2rem',
  fontSize: '1.2rem',
  color: '#666'
};

const errorStyle = {
  textAlign: 'center',
  padding: '2rem',
  fontSize: '1.2rem',
  color: '#dc3545',
  backgroundColor: '#f8d7da',
  borderRadius: '4px'
};

const noResultsStyle = {
  textAlign: 'center',
  padding: '2rem',
  fontSize: '1.2rem',
  color: '#666',
  backgroundColor: '#f8f9fa',
  borderRadius: '4px'
};

const profilesGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '2rem'
};

const profileCardStyle = {
  padding: '1.5rem',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e0e0e0',
  backgroundColor: 'white',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  ':hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
  }
};

const profileCardTopStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: '1rem'
};

const profilePhotoContainerStyle = {
  marginRight: '1rem'
};

const profilePhotoStyle = {
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '2px solid #4CAF50'
};

const profileCardHeaderStyle = {
  flex: 1
};

const profileNameStyle = {
  margin: 0,
  marginBottom: '0.25rem',
  fontSize: '1.2rem'
};

const licenseTypeStyle = {
  display: 'inline-block',
  fontSize: '0.8rem',
  backgroundColor: '#e0e0e0',
  color: '#333',
  padding: '0.2rem 0.5rem',
  borderRadius: '4px',
  marginTop: '0.25rem'
};

const specialtiesStyle = {
  margin: '0.5rem 0',
  fontSize: '0.9rem',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical'
};

const acceptingClientsContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  marginTop: '0.5rem'
};

const acceptingClientsIndicatorStyle = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  marginRight: '0.5rem'
};

const acceptingClientsTextStyle = {
  fontSize: '0.8rem',
  fontWeight: 'bold'
};

const viewProfileBtnContainerStyle = {
  marginTop: '1rem',
  display: 'flex',
  justifyContent: 'center'
};

const viewProfileBtnStyle = {
  backgroundColor: '#4CAF50',
  color: 'white',
  border: 'none',
  padding: '0.5rem 1rem',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  width: '100%',
  transition: 'background-color 0.2s ease',
  ':hover': {
    backgroundColor: '#45a049'
  }
};

// Modal Styles
const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000
};

const modalContentStyle = {
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '90vh',
  overflow: 'auto',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
};

const modalHeaderStyle = {
  padding: '1.5rem',
  borderBottom: '1px solid #e0e0e0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

const modalTitleStyle = {
  margin: 0,
  fontSize: '1.8rem',
  display: 'flex',
  alignItems: 'center'
};

const modalLicenseBadgeStyle = {
  fontSize: '0.9rem',
  backgroundColor: '#e0e0e0',
  color: '#333',
  padding: '0.2rem 0.6rem',
  borderRadius: '4px',
  marginLeft: '1rem',
  fontWeight: 'normal'
};

const modalCloseButtonStyle = {
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#666'
};

const modalBodyStyle = {
  padding: '1.5rem'
};

const modalProfileHeaderStyle = {
  display: 'flex',
  marginBottom: '2rem'
};

const modalPhotoContainerStyle = {
  marginRight: '2rem'
};

const modalPhotoStyle = {
  width: '120px',
  height: '120px',
  borderRadius: '50%',
  objectFit: 'cover',
  border: '3px solid #4CAF50'
};

const modalProfileInfoStyle = {
  flex: 1
};

const unavailableReasonStyle = {
  backgroundColor: '#fff3cd',
  padding: '0.75rem',
  borderRadius: '4px',
  marginTop: '0.5rem',
  fontSize: '0.9rem'
};

const detailItemStyle = {
  margin: '0.5rem 0',
  fontSize: '1rem'
};

const modalDetailSectionStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
  gap: '2rem'
};

const modalSectionStyle = {
  marginBottom: '1.5rem'
};

const sectionTitleStyle = {
  borderBottom: '2px solid #4CAF50',
  paddingBottom: '0.5rem',
  marginBottom: '1rem',
  fontSize: '1.2rem'
};

export default TeamDirectory;